import React, { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { X, RotateCw, ZoomIn, ZoomOut, Circle, Square } from 'lucide-react';

interface ImageCropperProps {
  image: File | string;
  isOpen: boolean;
  onClose: () => void;
  onCropComplete: (croppedImageBlob: Blob) => void;
  aspect?: number;
  cropShape?: 'rect' | 'round';
  outputSize?: { width: number; height: number };
  quality?: number;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

type DragMode = 'move' | 'resize-nw' | 'resize-ne' | 'resize-sw' | 'resize-se' | 'resize-n' | 'resize-s' | 'resize-w' | 'resize-e' | null;

const ImageCropper: React.FC<ImageCropperProps> = ({
  image,
  isOpen,
  onClose,
  onCropComplete,
  aspect = 1,
  cropShape = 'round',
  outputSize = { width: 800, height: 800 },
  quality = 0.9
}) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [imageSize, setImageSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [displaySize, setDisplaySize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [cropArea, setCropArea] = useState<CropArea | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragMode, setDragMode] = useState<DragMode>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [cropStart, setCropStart] = useState<CropArea | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFreestyle, setIsFreestyle] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{ x: number; y: number } | null>(null);
  const [cursorStyle, setCursorStyle] = useState<string>('crosshair');
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Convert File to data URL and load image
  useEffect(() => {
    if (!isOpen) return;

    const loadImage = async () => {
      let src = '';
      if (typeof image === 'string') {
        src = image;
      } else {
        src = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(image);
        });
      }

      setImageSrc(src);

      // Load image to get dimensions
      const img = new Image();
      img.onload = () => {
        setImageSize({ width: img.width, height: img.height });
        // Calculate display size to fit container
        const container = containerRef.current;
        if (container) {
          const containerWidth = container.clientWidth;
          const containerHeight = container.clientHeight;
          const scale = Math.min(
            containerWidth / img.width,
            containerHeight / img.height,
            1
          );
          const displayWidth = img.width * scale;
          const displayHeight = img.height * scale;
          setDisplaySize({ width: displayWidth, height: displayHeight });

          // Start with no crop area - user will select it
          setCropArea(null);
          setCursorStyle('crosshair');
          setShowPreview(false);
          setPreviewBlob(null);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
        }
      };
      img.src = src;
    };

    loadImage();
    setIsFreestyle(cropShape === 'rect');
  }, [image, isOpen, cropShape]);

  // Get current crop shape
  const currentCropShape = isFreestyle ? 'rect' : cropShape;
  const currentAspect = isFreestyle ? undefined : aspect;

  // Calculate crop area with aspect ratio constraint
  const constrainCropArea = useCallback((area: CropArea, maintainAspect: boolean, aspectRatio?: number): CropArea => {
    if (!maintainAspect || !aspectRatio) return area;

    const currentAspect = area.width / area.height;
    if (Math.abs(currentAspect - aspectRatio) < 0.01) return area;

    // Adjust to maintain aspect ratio
    if (currentAspect > aspectRatio) {
      // Too wide, adjust height
      return { ...area, height: area.width / aspectRatio };
    } else {
      // Too tall, adjust width
      return { ...area, width: area.height * aspectRatio };
    }
  }, []);

  // Check if point is in crop area
  const isInCropArea = useCallback((x: number, y: number, area: CropArea): boolean => {
    return x >= area.x && x <= area.x + area.width &&
           y >= area.y && y <= area.y + area.height;
  }, []);

  // Get resize handle at position
  const getResizeHandle = useCallback((x: number, y: number, area: CropArea): DragMode => {
    const handleSize = 15; // Increased handle size for easier clicking
    
    // Check corner handles first (they have priority)
    const cornerHandles = [
      { mode: 'resize-nw' as DragMode, x: area.x, y: area.y },
      { mode: 'resize-ne' as DragMode, x: area.x + area.width, y: area.y },
      { mode: 'resize-sw' as DragMode, x: area.x, y: area.y + area.height },
      { mode: 'resize-se' as DragMode, x: area.x + area.width, y: area.y + area.height },
    ];

    for (const handle of cornerHandles) {
      const dx = Math.abs(x - handle.x);
      const dy = Math.abs(y - handle.y);
      if (dx <= handleSize && dy <= handleSize) {
        return handle.mode;
      }
    }

    // Check edge handles (only if not on a corner)
    const edgeHandles = [
      { mode: 'resize-n' as DragMode, x: area.x + area.width / 2, y: area.y, check: (dx: number, dy: number) => Math.abs(dx) <= area.width / 2 && Math.abs(dy) <= handleSize },
      { mode: 'resize-s' as DragMode, x: area.x + area.width / 2, y: area.y + area.height, check: (dx: number, dy: number) => Math.abs(dx) <= area.width / 2 && Math.abs(dy) <= handleSize },
      { mode: 'resize-w' as DragMode, x: area.x, y: area.y + area.height / 2, check: (dx: number, dy: number) => Math.abs(dx) <= handleSize && Math.abs(dy) <= area.height / 2 },
      { mode: 'resize-e' as DragMode, x: area.x + area.width, y: area.y + area.height / 2, check: (dx: number, dy: number) => Math.abs(dx) <= handleSize && Math.abs(dy) <= area.height / 2 },
    ];

    for (const handle of edgeHandles) {
      const dx = x - handle.x;
      const dy = y - handle.y;
      if (handle.check(dx, dy)) {
        // Make sure we're not too close to a corner
        const isNearCorner = cornerHandles.some(corner => {
          const cornerDx = Math.abs(x - corner.x);
          const cornerDy = Math.abs(y - corner.y);
          return cornerDx <= handleSize && cornerDy <= handleSize;
        });
        if (!isNearCorner) {
          return handle.mode;
        }
      }
    }

    // Check if inside crop area for moving
    if (isInCropArea(x, y, area)) {
      return 'move';
    }

    return null;
  }, [isInCropArea]);

  // Get coordinates relative to the image (not container)
  const getImageCoordinates = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageRef.current || !containerRef.current) return { x: 0, y: 0 };

    const imageRect = imageRef.current.getBoundingClientRect();
    const x = e.clientX - imageRect.left;
    const y = e.clientY - imageRect.top;

    return { x, y };
  }, []);

  // Same as above but from client coordinates (for touch events)
  const getImageCoordinatesFromClient = useCallback((clientX: number, clientY: number) => {
    if (!imageRef.current) return { x: 0, y: 0 };
    const imageRect = imageRef.current.getBoundingClientRect();
    return {
      x: clientX - imageRect.left,
      y: clientY - imageRect.top
    };
  }, []);

  // Pointer down (shared by mouse and touch)
  const handlePointerDown = useCallback((x: number, y: number) => {
    if (!containerRef.current || !imageRef.current) return;

    if (x < 0 || y < 0 || x > displaySize.width || y > displaySize.height) return;

    if (cropArea) {
      const handle = getResizeHandle(x, y, cropArea);
      if (handle) {
        setIsDragging(true);
        setDragMode(handle);
        setDragStart({ x, y });
        setCropStart(cropArea);
        return;
      }
    }

    setIsSelecting(true);
    setSelectionStart({ x, y });
    setCropArea({ x, y, width: 0, height: 0 });
  }, [cropArea, getResizeHandle, displaySize]);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const { x, y } = getImageCoordinates(e);
    handlePointerDown(x, y);
  }, [getImageCoordinates, handlePointerDown]);

  // Refs for document-level touch handlers (so they see latest state)
  const pointerMoveRef = useRef<(x: number, y: number) => void>(() => {});
  const pointerUpRef = useRef<() => void>(() => {});

  // Mouse move handler
  const handlePointerMove = useCallback((x: number, y: number) => {
    if (!containerRef.current || !imageRef.current) return;

    // Update cursor style based on hover position (only when not dragging/selecting)
    if (!isDragging && !isSelecting && cropArea) {
      const handle = getResizeHandle(x, y, cropArea);
      if (handle === 'move') {
        setCursorStyle('move');
      } else if (handle === 'resize-nw' || handle === 'resize-se') {
        setCursorStyle('nwse-resize');
      } else if (handle === 'resize-ne' || handle === 'resize-sw') {
        setCursorStyle('nesw-resize');
      } else if (handle === 'resize-n' || handle === 'resize-s') {
        setCursorStyle('ns-resize');
      } else if (handle === 'resize-w' || handle === 'resize-e') {
        setCursorStyle('ew-resize');
      } else {
        setCursorStyle('crosshair');
      }
    } else if (!isDragging && !isSelecting && !cropArea) {
      setCursorStyle('crosshair');
    }

    if (isDragging && cropArea && cropStart && dragMode) {
      const dx = x - dragStart.x;
      const dy = y - dragStart.y;

      let newArea: CropArea = { ...cropStart };

      if (dragMode === 'move') {
        newArea.x = Math.max(0, Math.min(displaySize.width - newArea.width, cropStart.x + dx));
        newArea.y = Math.max(0, Math.min(displaySize.height - newArea.height, cropStart.y + dy));
      } else {
        // Resize based on handle - handle each direction separately
        const minSize = 20;
        
        if (dragMode === 'resize-nw') {
          // Top-left corner
          newArea.width = cropStart.width - dx;
          newArea.height = cropStart.height - dy;
          newArea.x = cropStart.x + dx;
          newArea.y = cropStart.y + dy;
        } else if (dragMode === 'resize-ne') {
          // Top-right corner
          newArea.width = cropStart.width + dx;
          newArea.height = cropStart.height - dy;
          newArea.y = cropStart.y + dy;
        } else if (dragMode === 'resize-sw') {
          // Bottom-left corner
          newArea.width = cropStart.width - dx;
          newArea.height = cropStart.height + dy;
          newArea.x = cropStart.x + dx;
        } else if (dragMode === 'resize-se') {
          // Bottom-right corner
          newArea.width = cropStart.width + dx;
          newArea.height = cropStart.height + dy;
        } else if (dragMode === 'resize-n') {
          // Top edge
          newArea.height = cropStart.height - dy;
          newArea.y = cropStart.y + dy;
        } else if (dragMode === 'resize-s') {
          // Bottom edge
          newArea.height = cropStart.height + dy;
        } else if (dragMode === 'resize-w') {
          // Left edge
          newArea.width = cropStart.width - dx;
          newArea.x = cropStart.x + dx;
        } else if (dragMode === 'resize-e') {
          // Right edge
          newArea.width = cropStart.width + dx;
        }

        // Constrain to image bounds and minimum size
        // First, ensure minimum size
        if (newArea.width < minSize) {
          if (dragMode.includes('w')) {
            newArea.x = cropStart.x + cropStart.width - minSize;
          }
          newArea.width = minSize;
        }
        if (newArea.height < minSize) {
          if (dragMode.includes('n')) {
            newArea.y = cropStart.y + cropStart.height - minSize;
          }
          newArea.height = minSize;
        }

        // Then constrain to image bounds
        if (newArea.x < 0) {
          newArea.width += newArea.x;
          newArea.x = 0;
        }
        if (newArea.y < 0) {
          newArea.height += newArea.y;
          newArea.y = 0;
        }
        if (newArea.x + newArea.width > displaySize.width) {
          newArea.width = displaySize.width - newArea.x;
        }
        if (newArea.y + newArea.height > displaySize.height) {
          newArea.height = displaySize.height - newArea.y;
        }

        // Ensure minimum size after bounds constraint
        if (newArea.width < minSize) {
          newArea.width = minSize;
          if (newArea.x + newArea.width > displaySize.width) {
            newArea.x = displaySize.width - minSize;
          }
        }
        if (newArea.height < minSize) {
          newArea.height = minSize;
          if (newArea.y + newArea.height > displaySize.height) {
            newArea.y = displaySize.height - minSize;
          }
        }

        // Apply aspect ratio if needed (only in circle mode)
        if (!isFreestyle && currentAspect) {
          newArea = constrainCropArea(newArea, true, currentAspect);
        }
      }

      setCropArea(newArea);
    } else if (isSelecting && selectionStart) {
      // Drawing new selection (like macOS screenshot - free rectangle)
      const newArea: CropArea = {
        x: Math.min(selectionStart.x, x),
        y: Math.min(selectionStart.y, y),
        width: Math.abs(x - selectionStart.x),
        height: Math.abs(y - selectionStart.y)
      };

      // Constrain to image bounds
      newArea.x = Math.max(0, Math.min(displaySize.width, newArea.x));
      newArea.y = Math.max(0, Math.min(displaySize.height, newArea.y));
      newArea.width = Math.max(0, Math.min(displaySize.width - newArea.x, newArea.width));
      newArea.height = Math.max(0, Math.min(displaySize.height - newArea.y, newArea.height));

      // Only apply aspect ratio if NOT in freestyle mode
      if (!isFreestyle && currentAspect) {
        // Maintain aspect ratio while dragging
        const currentAspectRatio = newArea.width / (newArea.height || 1);
        if (Math.abs(currentAspectRatio - currentAspect) > 0.01) {
          // Adjust to maintain aspect ratio
          if (newArea.width > newArea.height * currentAspect) {
            newArea.width = newArea.height * currentAspect;
          } else {
            newArea.height = newArea.width / currentAspect;
          }
        }
      }

      setCropArea(newArea);
    }
  }, [isDragging, isSelecting, cropArea, cropStart, dragStart, dragMode, selectionStart, displaySize, currentAspect, constrainCropArea, getResizeHandle, isFreestyle]);

  pointerMoveRef.current = handlePointerMove;

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const { x, y } = getImageCoordinates(e);
    handlePointerMove(x, y);
  }, [getImageCoordinates, handlePointerMove]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
    setIsSelecting(false);
    setDragMode(null);
    setSelectionStart(null);
    if (cropArea) {
      setCursorStyle('move');
    } else {
      setCursorStyle('crosshair');
    }
  }, [cropArea]);

  pointerUpRef.current = handlePointerUp;

  const handleMouseUp = useCallback(() => {
    handlePointerUp();
  }, [handlePointerUp]);

  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length !== 1) return;
    e.preventDefault();
    const { x, y } = getImageCoordinatesFromClient(e.touches[0].clientX, e.touches[0].clientY);
    handlePointerDown(x, y);

    const onTouchMove = (ev: TouchEvent) => {
      if (ev.touches.length !== 1) return;
      ev.preventDefault();
      const { x: mx, y: my } = getImageCoordinatesFromClient(ev.touches[0].clientX, ev.touches[0].clientY);
      pointerMoveRef.current(mx, my);
    };
    const onTouchEnd = () => {
      pointerUpRef.current();
      document.removeEventListener('touchmove', onTouchMove, { capture: true });
      document.removeEventListener('touchend', onTouchEnd, { capture: true });
      document.removeEventListener('touchcancel', onTouchEnd, { capture: true });
    };

    document.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
    document.addEventListener('touchend', onTouchEnd, { capture: true });
    document.addEventListener('touchcancel', onTouchEnd, { capture: true });
  }, [getImageCoordinatesFromClient, handlePointerDown]);

  // Get cursor style
  const getCursor = useCallback((x: number, y: number): string => {
    if (!cropArea) return 'crosshair';
    const handle = getResizeHandle(x, y, cropArea);
    if (!handle) return 'crosshair';
    if (handle === 'move') return 'move';
    if (handle.includes('nw') || handle.includes('se')) return 'nwse-resize';
    if (handle.includes('ne') || handle.includes('sw')) return 'nesw-resize';
    if (handle.includes('n') || handle.includes('s')) return 'ns-resize';
    if (handle.includes('w') || handle.includes('e')) return 'ew-resize';
    return 'default';
  }, [cropArea, getResizeHandle]);

  // Crop the image and show preview
  const handleCrop = async () => {
    if (!cropArea || !imageSrc || !imageSize || !displaySize) return;

    try {
      setIsProcessing(true);

      // Calculate actual pixel coordinates
      const scaleX = imageSize.width / displaySize.width;
      const scaleY = imageSize.height / displaySize.height;

      const pixelCrop = {
        x: cropArea.x * scaleX,
        y: cropArea.y * scaleY,
        width: cropArea.width * scaleX,
        height: cropArea.height * scaleY
      };

      // Create canvas and crop
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = imageSrc;
      });

      if (!img.naturalWidth || !img.naturalHeight) {
        throw new Error('Image has no dimensions (width or height is 0). The image may not be loaded or is invalid.');
      }

      const sourceW = Math.round(pixelCrop.width);
      const sourceH = Math.round(pixelCrop.height);
      if (sourceW < 1 || sourceH < 1) {
        throw new Error('Please select an area to crop by dragging on the image.');
      }

      if (!outputSize.width || !outputSize.height) {
        throw new Error('Output size has no dimensions (width or height is 0).');
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { 
        alpha: false,
        desynchronized: false,
        willReadFrequently: false
      });

      if (!ctx) throw new Error('No 2d context');

      // Enable high-quality rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Set canvas size to crop area - use rounded dimensions for pixel-perfect extraction
      const cropW = Math.round(pixelCrop.width);
      const cropH = Math.round(pixelCrop.height);
      canvas.width = cropW;
      canvas.height = cropH;

      // Enable high-quality rendering on source canvas
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw cropped portion - use precise coordinates to preserve pixel details
      // Round to nearest pixel for accurate extraction (sourceW/sourceH validated above)
      const sourceX = Math.round(pixelCrop.x);
      const sourceY = Math.round(pixelCrop.y);

      // Use exact pixel dimensions to avoid interpolation loss
      canvas.width = sourceW;
      canvas.height = sourceH;
      
      ctx.drawImage(
        img,
        sourceX, 
        sourceY, 
        sourceW, 
        sourceH,
        0, 
        0, 
        sourceW, 
        sourceH
      );

      // Calculate the best output size to preserve quality
      // Use the larger dimension to maintain quality, or use outputSize if crop is smaller
      const finalWidth = Math.max(pixelCrop.width, outputSize.width);
      const finalHeight = Math.max(pixelCrop.height, outputSize.height);

      // Apply shape (circle or rect)
      if (currentCropShape === 'round') {
        const outputCanvas = document.createElement('canvas');
        // Use outputSize for final dimensions, but scale from high-res crop
        outputCanvas.width = outputSize.width;
        outputCanvas.height = outputSize.height;
        const outputCtx = outputCanvas.getContext('2d', { 
          alpha: false,
          desynchronized: false,
          willReadFrequently: false
        });
        if (!outputCtx) throw new Error('No 2d context');

        // Enable high-quality rendering
        outputCtx.imageSmoothingEnabled = true;
        outputCtx.imageSmoothingQuality = 'high';

        // Draw circle mask
        outputCtx.beginPath();
        outputCtx.arc(outputSize.width / 2, outputSize.height / 2, Math.min(outputSize.width, outputSize.height) / 2, 0, 2 * Math.PI);
        outputCtx.clip();

        // Draw scaled image with high quality - scale from original crop to maintain detail
        outputCtx.drawImage(
          canvas, 
          0, 0, canvas.width, canvas.height,
          0, 0, outputSize.width, outputSize.height
        );

        // Convert to blob with maximum quality
        const blob = await new Promise<Blob>((resolve, reject) => {
          outputCanvas.toBlob(
            (b) => b ? resolve(b) : reject(new Error('Canvas is empty')),
            'image/jpeg',
            0.98 // Maximum quality to preserve pixel details
          );
        });
        
        // Show preview instead of uploading directly
        setPreviewBlob(blob);
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setShowPreview(true);
      } else {
        // Rectangular crop - resize to output size with high quality
        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = outputSize.width;
        outputCanvas.height = outputSize.height;
        const outputCtx = outputCanvas.getContext('2d', { 
          alpha: false,
          desynchronized: false,
          willReadFrequently: false
        });
        if (!outputCtx) throw new Error('No 2d context');

        // Enable high-quality rendering with best interpolation
        outputCtx.imageSmoothingEnabled = true;
        outputCtx.imageSmoothingQuality = 'high';

        // Draw with high quality scaling - preserve aspect ratio and detail
        outputCtx.drawImage(
          canvas,
          0, 0, canvas.width, canvas.height,
          0, 0, outputSize.width, outputSize.height
        );

        const blob = await new Promise<Blob>((resolve, reject) => {
          outputCanvas.toBlob(
            (b) => b ? resolve(b) : reject(new Error('Canvas is empty')),
            'image/jpeg',
            0.98 // Maximum quality to preserve pixel details
          );
        });
        
        // Show preview instead of uploading directly
        setPreviewBlob(blob);
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setShowPreview(true);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Something went wrong while cropping. Please try again.';
      toast.error(message);
      console.warn('Error cropping image:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle upload from preview
  const handleUpload = () => {
    if (previewBlob) {
      onCropComplete(previewBlob);
      // Clean up preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      onClose();
    }
  };

  // Handle back to crop from preview
  const handleBackToCrop = () => {
    setShowPreview(false);
    // Clean up preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPreviewBlob(null);
  };

  const handleReset = () => {
    // Clear selection - user needs to select again
    setCropArea(null);
    setIsFreestyle(cropShape === 'rect');
    // Clean up preview if exists
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPreviewBlob(null);
    setShowPreview(false);
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-75 select-none"
      style={{ userSelect: 'none' }}
      onDragStart={(e) => e.preventDefault()}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col select-none" style={{ userSelect: 'none' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {showPreview ? 'Preview' : 'Crop Profile Picture'}
          </h2>
          <div className="flex items-center gap-3">
            {/* Crop Shape Toggle */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setIsFreestyle(false)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  !isFreestyle
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                aria-label="Circular crop"
              >
                <Circle size={16} />
                <span>Circle</span>
              </button>
              <button
                onClick={() => setIsFreestyle(true)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  isFreestyle
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                aria-label="Freestyle crop"
              >
                <Square size={16} />
                <span>Freestyle</span>
              </button>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Preview or Cropper Container */}
        {showPreview && previewUrl ? (
          <div className="relative flex-1 min-h-[400px] bg-gray-900 flex items-center justify-center overflow-hidden">
            <div className="flex flex-col items-center gap-4 p-8 w-full h-full">
              <div className="relative flex items-center justify-center w-full h-full">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="object-contain rounded-lg shadow-lg"
                  style={{
                    borderRadius: currentCropShape === 'round' ? '50%' : '8px',
                    maxWidth: 'min(500px, 90vw)',
                    maxHeight: 'min(500px, 60vh)',
                    width: 'auto',
                    height: 'auto',
                    imageRendering: 'auto',
                    // WebkitImageRendering: 'auto',
                    // msInterpolationMode: 'bicubic'
                  }}
                />
              </div>
              <p className="text-sm text-gray-400 text-center">
                Preview of your cropped image
              </p>
            </div>
          </div>
        ) : (
          <div
            ref={containerRef}
            className="relative flex-1 min-h-[400px] bg-gray-900 flex items-center justify-center overflow-hidden select-none"
            style={{ cursor: cursorStyle, userSelect: 'none', touchAction: 'none' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={() => {
              handleMouseUp();
              setCursorStyle('crosshair');
            }}
            onTouchStart={handleTouchStart}
            onDragStart={(e) => e.preventDefault()}
            onDrag={(e) => e.preventDefault()}
          >
            {imageSrc && (
            <>
              <img
                ref={imageRef}
                src={imageSrc}
                alt="Crop"
                className="max-w-full max-h-full object-contain select-none"
                style={{ 
                  width: displaySize.width, 
                  height: displaySize.height,
                  userSelect: 'none',
                  pointerEvents: 'none'
                }}
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
              />
              
              {/* Overlay with crop area */}
              {cropArea && imageRef.current && (() => {
                const imageRect = imageRef.current!.getBoundingClientRect();
                const containerRect = containerRef.current!.getBoundingClientRect();
                const imageOffsetX = imageRect.left - containerRect.left;
                const imageOffsetY = imageRect.top - containerRect.top;
                
                return (
                  <>
                    {/* Dark overlay - positioned over the image (subtle, not blue) */}
                    <div
                      className="absolute pointer-events-none"
                      style={{
                        left: imageOffsetX,
                        top: imageOffsetY,
                        width: displaySize.width,
                        height: displaySize.height,
                        background: `linear-gradient(to right, 
                          rgba(0,0,0,0.3) 0%, 
                          rgba(0,0,0,0.3) ${(cropArea.x / displaySize.width) * 100}%, 
                          transparent ${(cropArea.x / displaySize.width) * 100}%, 
                          transparent ${((cropArea.x + cropArea.width) / displaySize.width) * 100}%, 
                          rgba(0,0,0,0.3) ${((cropArea.x + cropArea.width) / displaySize.width) * 100}%, 
                          rgba(0,0,0,0.3) 100%),
                          linear-gradient(to bottom, 
                          rgba(0,0,0,0.3) 0%, 
                          rgba(0,0,0,0.3) ${(cropArea.y / displaySize.height) * 100}%, 
                          transparent ${(cropArea.y / displaySize.height) * 100}%, 
                          transparent ${((cropArea.y + cropArea.height) / displaySize.height) * 100}%, 
                          rgba(0,0,0,0.3) ${((cropArea.y + cropArea.height) / displaySize.height) * 100}%, 
                          rgba(0,0,0,0.3) 100%)`
                      }}
                    />
                  
                  {/* Crop area border */}
                  {imageRef.current && (() => {
                    const imageRect = imageRef.current!.getBoundingClientRect();
                    const containerRect = containerRef.current!.getBoundingClientRect();
                    const imageOffsetX = imageRect.left - containerRect.left;
                    const imageOffsetY = imageRect.top - containerRect.top;
                    
                    return (
                      <div
                        className="absolute border-2 border-white pointer-events-none"
                        style={{
                          left: imageOffsetX + cropArea.x,
                          top: imageOffsetY + cropArea.y,
                          width: cropArea.width,
                          height: cropArea.height,
                          borderRadius: currentCropShape === 'round' ? '50%' : '0',
                          boxShadow: '0 0 0 1px rgba(0,0,0,0.3)'
                        }}
                      />
                    );
                  })()}
                  
                  {/* Resize handles */}
                  {currentCropShape === 'rect' && imageRef.current && (() => {
                    const imageRect = imageRef.current!.getBoundingClientRect();
                    const containerRect = containerRef.current!.getBoundingClientRect();
                    const imageOffsetX = imageRect.left - containerRect.left;
                    const imageOffsetY = imageRect.top - containerRect.top;
                    
                    return (
                      <>
                        {/* Corner handles */}
                        <div className="absolute w-3 h-3 bg-white border border-gray-400 rounded-sm" style={{ left: imageOffsetX + cropArea.x - 6, top: imageOffsetY + cropArea.y - 6, cursor: 'nwse-resize' }} />
                        <div className="absolute w-3 h-3 bg-white border border-gray-400 rounded-sm" style={{ left: imageOffsetX + cropArea.x + cropArea.width - 3, top: imageOffsetY + cropArea.y - 6, cursor: 'nesw-resize' }} />
                        <div className="absolute w-3 h-3 bg-white border border-gray-400 rounded-sm" style={{ left: imageOffsetX + cropArea.x - 6, top: imageOffsetY + cropArea.y + cropArea.height - 3, cursor: 'nesw-resize' }} />
                        <div className="absolute w-3 h-3 bg-white border border-gray-400 rounded-sm" style={{ left: imageOffsetX + cropArea.x + cropArea.width - 3, top: imageOffsetY + cropArea.y + cropArea.height - 3, cursor: 'nwse-resize' }} />
                        {/* Edge handles */}
                        <div className="absolute w-3 h-3 bg-white border border-gray-400 rounded-sm" style={{ left: imageOffsetX + cropArea.x + cropArea.width / 2 - 6, top: imageOffsetY + cropArea.y - 6, cursor: 'ns-resize' }} />
                        <div className="absolute w-3 h-3 bg-white border border-gray-400 rounded-sm" style={{ left: imageOffsetX + cropArea.x + cropArea.width / 2 - 6, top: imageOffsetY + cropArea.y + cropArea.height - 3, cursor: 'ns-resize' }} />
                        <div className="absolute w-3 h-3 bg-white border border-gray-400 rounded-sm" style={{ left: imageOffsetX + cropArea.x - 6, top: imageOffsetY + cropArea.y + cropArea.height / 2 - 6, cursor: 'ew-resize' }} />
                        <div className="absolute w-3 h-3 bg-white border border-gray-400 rounded-sm" style={{ left: imageOffsetX + cropArea.x + cropArea.width - 3, top: imageOffsetY + cropArea.y + cropArea.height / 2 - 6, cursor: 'ew-resize' }} />
                      </>
                    );
                  })()}
                  </>
                );
              })()}
            </>
          )}
          </div>
        )}

        {/* Controls */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          {!showPreview && (
            <div className="mb-4 text-sm text-gray-600">
              {!cropArea ? (
                <p className="text-gray-700 font-medium">Click and drag on the image to select an area</p>
              ) : (
                <>
                  <p>Drag the selection to move it, or drag handles to resize</p>
                  {!isFreestyle && currentAspect && (
                    <p className="text-xs mt-1">Aspect ratio locked: {currentAspect.toFixed(2)}:1</p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Action Buttons */}
          {showPreview ? (
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={handleBackToCrop}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Back to Crop
              </button>
              <button
                onClick={handleUpload}
                disabled={!previewBlob}
                className="px-4 py-2 text-sm font-medium text-white bg-black rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Upload
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Reset
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCrop}
                disabled={isProcessing || !cropArea}
                className="px-4 py-2 text-sm font-medium text-white bg-black rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Processing...
                  </>
                ) : (
                  'Crop & Preview'
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
